import React, { useEffect, useRef, useState } from 'react';
import { assets } from '../data/mock';
import { useTheme } from '../ThemeContext';
import PriceUrgency from './CountdownTimer';
import { useCheckout } from '../CheckoutContext';
import { useT } from '../i18n/useT';
import { useLang } from '../i18n/LanguageContext';

const HeroSection = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const { isDark } = useTheme();
  const { startCheckout } = useCheckout();
  const t = useT();
  const { lang } = useLang();
  const videoSrc = lang === 'en' ? '/openbro-motion.mp4' : '/openbro-motion-fr.mp4';

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !videoPlaying;
    video.play().catch(() => {});
  }, [videoPlaying, videoSrc]);
  const tc = isDark ? '#FFFFFF' : '#1B2A4A';

  return (
    <section className="relative">
      <div className="mx-auto max-w-[1140px] px-4">
        <h1
          className="text-center mt-2 text-[13px] tracking-[0.35em] font-medium uppercase transition-colors duration-300"
          style={{ fontFamily: '"Inter", sans-serif', color: isDark ? '#E6EBF2' : '#1B2A4A' }}
        >
          {t.hero.brandName}
        </h1>
        <div className="relative mt-2 flex justify-center">
          <h2
            className="z-10 relative text-center capitalize text-[42px] lg:text-[80px] font-bold leading-[1.1] transition-colors duration-300"
            style={{ fontFamily: '"Space Grotesk", sans-serif', color: tc }}
          >
            {t.hero.headline}
          </h2>
          <img alt="Green Underline" loading="lazy" width="768" height="31" className="absolute bottom-[-10px] lg:bottom-[-30px] z-0 max-w-[320px] lg:max-w-[768px]" src={assets.underline} style={{ right: 'calc(50% - 160px)', filter: isDark ? 'hue-rotate(140deg) brightness(1.5)' : 'none' }} />
        </div>
        <p
          className="copy-text text-center max-w-lg mx-auto w-full mt-5 text-[15px] lg:text-[16px] transition-colors duration-300"
          style={{ fontFamily: '"Inter", sans-serif', color: isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A' }}
          dangerouslySetInnerHTML={{ __html: t.hero.subtitle1 }}
        />
        <p className="text-center max-w-xl mx-auto w-full mt-2 text-[15px] lg:text-[16px] transition-colors duration-300" style={{ fontFamily: '"Inter", sans-serif', color: isDark ? 'rgba(255,255,255,0.7)' : '#1B2A4A' }}>
          {t.hero.subtitle2.before}
          <span className="font-black" style={{ color: isDark ? '#FFFFFF' : undefined }}>{t.hero.subtitle2.bold1}</span>
          {t.hero.subtitle2.mid1}
          <span className="font-black" style={{ color: isDark ? '#FFFFFF' : undefined }}>{t.hero.subtitle2.bold2}</span>
          {t.hero.subtitle2.mid2}
          <span className="font-black" style={{ color: isDark ? '#FFFFFF' : undefined }}>{t.hero.subtitle2.bold3}</span>
        </p>

        {/* Video Player */}
        <div className="relative z-30 mt-6">
          <div
            className={`border-[3px] p-[1px] transition-all duration-300 ${isDark ? 'tech-glow' : ''}`}
            style={{ borderColor: isDark ? 'rgba(255,255,255,0.55)' : tc }}
          >
            <div className="relative" style={{ background: isDark ? 'rgba(0,0,0,0.6)' : 'linear-gradient(rgba(255,255,255,0.31) 0%, rgba(255,255,255,0.11) 100%)' }}>
              <div className="min-h-[280px] lg:min-h-[618px] w-full relative">
                <video
                  ref={videoRef}
                  src={videoSrc}
                  className="absolute inset-0 h-full w-full object-cover"
                  autoPlay
                  muted={!videoPlaying}
                  loop
                  playsInline
                  preload="auto"
                  controls={videoPlaying}
                  controlsList="nodownload noplaybackrate noremoteplayback"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                />
                {!videoPlaying && (
                  <div className="absolute top-0 left-0 h-full w-full bg-[rgba(2,4,9,.35)] cursor-pointer flex items-center justify-center" onClick={() => setVideoPlaying(true)}>
                    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" className="h-[65px] w-[65px] lg:h-[83px] lg:w-[83px] text-white border-white/80 border-[2px] transition-all duration-300 rounded-full" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zM188.3 147.1c-7.6 4.2-12.3 12.3-12.3 20.9V344c0 8.7 4.7 16.7 12.3 20.9s16.8 4.1 24.3-.5l144-88c7.1-4.4 11.5-12.1 11.5-20.5s-4.4-16.1-11.5-20.5l-144-88c-7.4-4.5-16.7-4.7-24.3-.5z" /></svg>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Area */}
        <div className="flex flex-col justify-center items-center">
          <div className="mt-6">
            <PriceUrgency />
          </div>
          <div className="flex flex-col lg:flex-row mt-5 relative gap-4 lg:gap-6 items-center w-full lg:w-auto">
            <img alt="Scribble" loading="lazy" width="108" height="72" className="hidden lg:block absolute left-[-142px] top-0 z-20" src={assets.arrowLeft} style={{ filter: isDark ? 'invert(1) hue-rotate(180deg) brightness(0.8)' : 'none' }} />
            <img alt="Scribble" loading="lazy" width="108" height="72" className="hidden lg:block absolute right-[-142px] top-0 z-20" src={assets.arrowRight} style={{ filter: isDark ? 'invert(1) hue-rotate(180deg) brightness(0.8)' : 'none' }} />
            <div className="w-full lg:w-auto cursor-pointer" onClick={() => startCheckout('hero_cta')}>
              <button className={`cta-urgent cta-glow-ring w-full lg:w-min lg:min-w-[266px] text-xl border-2 p-[1px] group duration-300 transition-all font-bold ${isDark ? 'tech-glow' : ''}`} style={{ backgroundColor: isDark ? '#020409' : 'white', borderColor: isDark ? 'rgba(255,255,255,0.7)' : tc }}>
                <div className="py-3.5 px-9 group-hover:bg-transparent duration-300 transition-all m-[1px]" style={{ backgroundColor: isDark ? '#F4F7FB' : tc, color: isDark ? '#020409' : 'white' }}>
                  {t.hero.cta}
                </div>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 pointer-events-none mt-4">
            <img alt="Enrolled People" loading="lazy" width="100" height="43" src={assets.enrolledAvatars} />
            <h4 className="text-[14px] font-bold transition-colors duration-300" style={{ fontFamily: '"Space Grotesk", sans-serif', color: isDark ? '#FFFFFF' : 'black' }}>{t.hero.enrolled}</h4>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
